<template>
  <div class="home-page">
    <div v-if="heroIndex === 2" class="hero-container" :style="heroBackgroundStyle">
      <div class="hero-content">
        <div class="definition">
          <audio id="audio-player" hidden>
            <source src="../assets/reckon.mp3" type="audio/mpeg" />
            <track kind="captions" />
          </audio>
          <v-item-group>
            <v-container>
              <v-row>
                <v-col xs="12" cols="8">
                  <v-row class="d-inline-block pl-4">
                    <v-row class="align-baseline mb-2">
                      <div class="mr-3 definition-header">reckon</div>
                      <h6 class="definition-verb">verb</h6>
                    </v-row>
                    <v-row class="align-center">
                      <span class="syllables mr-6">reck-on</span>
                      <button
                        type="button"
                        class="syllables-audio rounded-pill pa-3"
                        aria-label="Play the pronunciation of reckon"
                        @click="playAudio"
                        @keydown="handleKeydown"
                      >
                        ˈre-kən
                        <v-icon>mdi-volume-high</v-icon>
                      </button>
                    </v-row>
                    <v-row class="align-center text-h6 text-md-h5 text-lg-h4 mt-6">
                      <span class="syllables mr-6">reckoned; reckoning</span>
                    </v-row>
                    <v-row class="definition-meaning mt-3">
                      <p><strong>1.</strong> To believe something is true.</p>
                    </v-row>
                    <v-row class="definition-meaning left-border">
                      <span>We <i>reckon</i> there is no better company.</span>
                    </v-row>
                    <v-row class="definition-meaning mt-6">
                      <p><strong>2.</strong> To make a calculation.</p>
                    </v-row>
                    <v-row class="definition-meaning left-border">
                      <span>We <i>reckoned</i> that it was time for a change.</span>
                    </v-row>
                  </v-row>
                </v-col>
                <v-col cols="4" class="d-none d-sm-block">
                  <v-img height="300" contain :src="reckonBallLogo | mediaSrc" />
                </v-col>
              </v-row>
            </v-container>
          </v-item-group>
        </div>
      </div>
    </div>

    <div v-if="heroIndex === 1" class="hero-container-2" :style="skyFightBackgroundStyle">
      <div class="hero-content-2">
        <v-container>
          <v-row>
            <v-col cols="12" class="hero-2-title-container">
              <div><span class="hero-2-title">RECKON IS READY</span></div>
              <div class="mt-6">
                <v-btn color="red" @click="navigateTo('contact')">Contact Us</v-btn>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </div>

    <button
      type="button"
      class="home-transition"
      aria-label="Continue to Why We Exist"
      aria-controls="why-we-exist"
      @click="scrollToSection('why-we-exist')"
    >
      <span>Built on belief. Proven through the work. Remembered through the people.</span>
      <v-icon color="red">mdi-arrow-down</v-icon>
    </button>

    <div class="home-narrative">
      <section
        id="why-we-exist"
        class="home-section home-section--dark"
        aria-labelledby="why-we-exist-title"
      >
        <v-container class="home-section__container">
          <v-row align="center">
            <v-col cols="12" md="6" class="home-section__copy">
              <p class="home-section__eyebrow">Our Purpose</p>
              <h2 id="why-we-exist-title" class="home-section__title">Why We Exist</h2>
              <div class="home-section__body home-section__body--stacked">
                <p>Technology evolves every day, but great teams are built over time.</p>
                <p>
                  At Reckon, we believe exceptional software begins with exceptional people.
                  That's why we invest in more than technical expertise, we invest in the
                  relationships, experiences, and opportunities that help our team grow together.
                </p>
                <p>
                  From tackling mission-critical challenges for our customers to exploring new
                  ideas at conferences, celebrating traditions, and embarking on shared adventures,
                  every experience strengthens the people behind the code.
                </p>
                <p>
                  The result is a team that brings curiosity, trust, and technical excellence to
                  every mission we support.
                </p>
              </div>
              <div class="home-section__actions">
                <v-btn outlined large @click="navigateTo('beliefs')">What We Believe</v-btn>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <figure class="purpose-visual">
                <v-img
                  class="purpose-visual__image"
                  aspect-ratio="0.8"
                  :src="whyWeExistImage | mediaSrc"
                  role="img"
                  aria-label="A collage of the Reckon Crew sharing trips, meals, and team experiences"
                />
                <figcaption>Built through shared experiences.</figcaption>
              </figure>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <button
        type="button"
        class="home-transition"
        aria-label="Continue to meet the Reckon crew"
        aria-controls="who-we-are"
        @click="scrollToSection('who-we-are')"
      >
        <span>Exceptional work starts with exceptional people. Meet the crew.</span>
        <v-icon color="red">mdi-arrow-down</v-icon>
      </button>

      <section
        id="who-we-are"
        class="home-section home-section--charcoal home-section--crew"
        aria-labelledby="who-we-are-title"
      >
        <v-container class="home-section__container">
          <v-row align="start">
            <v-col cols="12" md="7">
              <button
                type="button"
                class="team-image-button"
                aria-label="Open the Reckon Crew photo"
                @click="showLightbox = true"
              >
                <v-img
                  class="split-section-image"
                  aspect-ratio="1.55"
                  :src="reckonCrewImage | mediaSrc"
                />
              </button>
            </v-col>
            <v-col cols="12" md="5" class="home-section__copy split-copy--right">
              <p class="home-section__eyebrow">The Crew</p>
              <h2 id="who-we-are-title" class="home-section__title">Who Is Reckon?</h2>
              <div class="home-section__body home-section__body--stacked">
                <p>
                  Reckon is a team of software engineers, architects, testers, DevSecOps
                  specialists, and problem-solvers united by a shared commitment to doing
                  meaningful work exceptionally well.
                </p>
                <p>
                  We bring curiosity, ownership, and technical expertise to every mission. Whether
                  we are building software, supporting customers, sharing ideas, or exploring
                  somewhere new together, we trust one another, keep learning, and grow as
                  engineers, teammates, and partners. Together, we are a force to be reckoned with.
                </p>
              </div>
              <div class="home-section__actions">
                <v-btn outlined large @click="navigateTo('reckon-way-people')">Meet the Heroes</v-btn>
                <v-btn outlined large @click="navigateTo('beginnings')">The Reckoning</v-btn>
              </div>
            </v-col>
          </v-row>
        </v-container>
        <vue-easy-lightbox
          :visible="showLightbox"
          :imgs="reckonCrewLightboxImages"
          @hide="showLightbox = false"
        />
      </section>

      <button
        type="button"
        class="home-transition"
        aria-label="Continue to The Reckon Way"
        aria-controls="reckon-way"
        @click="scrollToSection('reckon-way')"
      >
        <span>Strong teams grow through shared experiences. Discover the Reckon Way.</span>
        <v-icon color="red">mdi-arrow-down</v-icon>
      </button>

      <section
        id="reckon-way"
        class="home-section home-section--editorial"
        aria-labelledby="reckon-way-title"
      >
        <v-container class="home-section__container">
          <div class="editorial-intro">
            <p class="home-section__eyebrow">How We Grow</p>
            <h2 id="reckon-way-title" class="home-section__title">The Reckon Way</h2>
            <div class="home-section__body home-section__body--stacked">
              <p>At Reckon, we believe the strongest teams are built through shared experiences.</p>
              <p>
                The Reckon Way is our commitment to continuous learning, meaningful traditions,
                and adventures that bring us together. From industry conferences and technical
                workshops to team retreats, local traditions, and celebrating milestones, every
                experience strengthens our relationships and broadens our perspective.
              </p>
              <p>
                These moments aren't separate from our work. They shape how we communicate,
                collaborate, solve problems, and support one another. The trust we build outside
                the office becomes the foundation for the work we do alongside our customers.
              </p>
              <p>
                Because building exceptional software starts with building exceptional people.
              </p>
            </div>
            <div class="home-section__actions">
              <v-btn outlined large @click="navigateTo('/the-reckon-way')">Explore The Reckon Way</v-btn>
            </div>
          </div>

          <div class="reckon-way-grid">
            <article
              v-for="story in reckonWayStories"
              :key="story.title"
              class="story-card"
            >
              <button type="button" class="story-card__link" @click="navigateTo(story.destination)">
                <v-img
                  class="story-card__image"
                  :aspect-ratio="1.4"
                  :src="story.image | mediaSrc"
                />
                <span class="story-card__content">
                  <span class="story-card__category">{{ story.category }}</span>
                  <span class="story-card__title">{{ story.title }}</span>
                  <span class="story-card__cta">Explore <v-icon small>mdi-arrow-right</v-icon></span>
                </span>
              </button>
            </article>
          </div>
        </v-container>
      </section>

      <button
        type="button"
        class="home-transition"
        aria-label="Continue to Engineering Capabilities"
        aria-controls="capabilities"
        @click="scrollToSection('capabilities')"
      >
        <span>Culture builds the team. The team delivers the mission.</span>
        <v-icon color="red">mdi-arrow-down</v-icon>
      </button>

      <section
        id="capabilities"
        class="home-section home-section--charcoal"
        aria-labelledby="capabilities-title"
      >
        <v-container class="home-section__container">
          <div class="centered-intro">
            <p class="home-section__eyebrow">What We Do</p>
            <h2 id="capabilities-title" class="home-section__title">Engineering Capabilities</h2>
            <div class="home-section__body home-section__body--stacked">
              <p>
                We partner with government and commercial organizations to design, build,
                modernize, and support mission-critical software systems.
              </p>
              <p>
                Our multidisciplinary teams deliver secure, scalable, and reliable solutions
                across the entire software lifecycle. Whether developing new applications,
                modernizing legacy systems, implementing cloud-native architectures, or improving
                software quality through automation, we help our customers solve complex technical
                challenges with confidence.
              </p>
              <p>
                Our capabilities span software and systems engineering, cloud solutions, DevSecOps,
                quality engineering, program delivery, and data and AI. Backed by a culture of
                continuous learning and collaboration, we bring the technical expertise and trusted
                partnership our customers need to accomplish their mission.
              </p>
            </div>
          </div>

          <div class="capability-grid">
            <article v-for="capability in capabilities" :key="capability.title" class="home-card capability-card">
              <button type="button" class="capability-card__link" @click="navigateTo(capability.route)">
                <span class="capability-card__number">{{ capability.number }}</span>
                <span class="capability-card__title">{{ capability.title }}</span>
                <span class="capability-card__identity">{{ capability.heroLabel }}</span>
                <span class="capability-card__description">{{ capability.description }}</span>
                <span class="capability-card__cta">Explore <v-icon small>mdi-arrow-right</v-icon></span>
              </button>
            </article>
          </div>

          <div class="home-section__actions home-section__actions--centered">
            <v-btn
              outlined
              large
              @click="navigateTo('capabilities')"
            >
              Explore Engineering Capabilities
            </v-btn>
          </div>
        </v-container>
      </section>

      <button
        type="button"
        class="home-transition"
        aria-label="Continue to stories and experiences"
        aria-controls="stories-and-experiences"
        @click="scrollToSection('stories-and-experiences')"
      >
        <span>Expertise proves what we can do. Experience shows who we are.</span>
        <v-icon color="red">mdi-arrow-down</v-icon>
      </button>

      <section
        id="stories-and-experiences"
        class="home-section home-section--dark"
        aria-labelledby="built-along-way-title"
      >
        <v-container class="home-section__container">
          <div class="stories-heading">
            <div>
              <p class="home-section__eyebrow">Stories &amp; Experiences</p>
              <h2 id="built-along-way-title" class="home-section__title">Built Along the Way</h2>
              <div class="home-section__body home-section__body--stacked">
                <p>The Reckon Way comes to life through the experiences we share.</p>
                <p>
                  From AWS re:Invent and SXSW London to Iceland, local traditions, anniversary
                  dinners, and Friday lunches, every journey adds another chapter to our story.
                  Each experience leaves us with new ideas, stronger relationships, and memories
                  that continue to shape the way we work together.
                </p>
                <p>
                  These stories aren't just highlights from our calendar—they're part of the
                  foundation that makes Reckon who we are.
                </p>
                <p>
                  Explore the places we've been, the traditions we've created, and the moments that
                  continue to define our journey.
                </p>
              </div>
            </div>
          </div>

          <div class="adventure-grid">
            <article v-for="story in adventureStories" :key="story.title" class="home-card adventure-card">
              <button type="button" class="adventure-card__link" @click="navigateTo(story.destination)">
                <v-img class="home-card__image" aspect-ratio="1.35" :src="story.image | mediaSrc" />
                <span class="home-card__content">
                  <span class="story-card__category">{{ story.type }}</span>
                  <span class="adventure-card__title">{{ story.title }}</span>
                  <span class="adventure-card__cta">Read the Story <v-icon small>mdi-arrow-right</v-icon></span>
                </span>
              </button>
            </article>
          </div>

          <div class="home-section__actions home-section__actions--centered">
            <v-btn outlined large @click="navigateTo('/the-reckon-way/stories')">Explore All Stories</v-btn>
          </div>
        </v-container>
      </section>

      <button
        type="button"
        class="home-transition"
        aria-label="Continue to Reckon careers"
        aria-controls="careers"
        @click="scrollToSection('careers')"
      >
        <span>Every journey adds to the story. Your next one could start here.</span>
        <v-icon color="red">mdi-arrow-down</v-icon>
      </button>

      <section
        id="careers"
        class="home-section home-section--charcoal"
        aria-labelledby="careers-title"
      >
        <v-container class="home-section__container">
          <v-row align="start">
            <v-col cols="12" md="6" order="1" order-md="2">
              <v-img
                class="split-section-image"
                aspect-ratio="1.35"
                :src="weWantYouImage | mediaSrc"
              />
            </v-col>
            <v-col cols="12" md="6" order="2" order-md="1" class="home-section__copy split-copy--left">
              <p class="home-section__eyebrow">Careers</p>
              <h2 id="careers-title" class="home-section__title">Why Join Reckon?</h2>
              <div class="home-section__body home-section__body--stacked">
                <p>
                  We're building more than software—we're building careers, friendships, and a
                  team that's always learning.
                </p>
                <p>
                  Work alongside talented engineers on meaningful problems, grow through
                  mentorship, conferences, and shared experiences, and join a place where your
                  ideas matter and your teammates genuinely enjoy the journey.
                </p>
                <p>Your next adventure starts here.</p>
              </div>
              <div class="home-section__actions">
                <v-btn outlined large @click="navigateTo('benefits')">Learn More</v-btn>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <button
        type="button"
        class="home-transition"
        aria-label="Continue to contact Reckon"
        aria-controls="contact-reckon"
        @click="scrollToSection('contact-reckon')"
      >
        <span>Ready to build something that matters? Send the signal.</span>
        <v-icon color="red">mdi-arrow-down</v-icon>
      </button>

      <section
        id="contact-reckon"
        class="home-section final-cta"
        aria-labelledby="final-cta-title"
      >
        <v-container class="home-section__container final-cta__content">
          <h2 id="final-cta-title" class="home-section__title">Need a Hero?</h2>
          <div class="home-section__body home-section__body--stacked">
            <p>Every great mission starts with a signal.</p>
            <p>
              Whether you're looking for a trusted technology partner or you're ready for your next
              adventure, we're here to help. Reach out, tell us your story, and let's explore what
              we can build together.
            </p>
            <p><em>You know how to call us.</em></p>
          </div>
          <div class="home-section__actions home-section__actions--centered">
            <v-btn large color="red" @click="navigateTo('contact')">Light the Signal</v-btn>
            <v-btn large outlined @click="navigateTo('benefits')">Explore Careers</v-btn>
          </div>
        </v-container>
      </section>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import { mediaRef } from '@/services/mediaAssets';
import { capabilities as capabilityCatalog } from '@/content/capabilities';
import { publishedNarratives } from '@/content/culture/narratives';
import VueEasyLightbox from 'vue-easy-lightbox';
import AppFooter from './Footer.vue';

const homepageNarratives = publishedNarratives
  .filter((story) => !story.featured)
  .slice(0, 6)
  .map((story) => ({
    title: story.title,
    type: story.category,
    image: story.image,
    destination: story.href,
  }));

export default {
  name: 'HomePage',
  components: {
    AppFooter,
    VueEasyLightbox,
  },
  data: () => ({
    reckonBallLogo: mediaRef('reckon_ball_logo.png'),
    reckonCrewImage: mediaRef('reckon_crew.jpeg'),
    whyWeExistImage: mediaRef('homepage/why-we-exist-shared-experiences.png'),
    weWantYouImage: mediaRef('we_want_you.jpeg'),
    heroBackground: mediaRef('reckon_hero_bg.png'),
    skyFightBackground: mediaRef('sky_fight.webp'),
    heroIndex: 2,
    showLightbox: false,
    capabilities: capabilityCatalog.map((capability) => ({
      ...capability,
      destination: capability.route,
    })),
    reckonWayStories: [
      {
        title: 'Fire, Ice, and the Reckon Way',
        category: 'Corporate Retreat',
        image: mediaRef('homepage/iceland-featured-collage.png'),
        destination: '/the-reckon-way/iceland-corporate-retreat-2026',
      },
      {
        title: 'Reckon Ball 2025',
        category: 'Tradition',
        image: mediaRef('homepage/reckon-ball-2025-collage.png'),
        destination: '/the-reckon-way/reckon-ball/2025',
      },
      {
        title: 'AWS re:Invent',
        category: 'Conference',
        image: mediaRef('homepage/aws-reinvent-collage.png'),
        destination: '/the-reckon-way/aws-reinvent-las-vegas',
      },
    ],
    adventureStories: homepageNarratives,
  }),
  computed: {
    reckonCrewLightboxImages() {
      const source = this.$options.filters.mediaSrc(this.reckonCrewImage);
      return source ? [source] : [];
    },
    heroBackgroundStyle() {
      const source = this.$options.filters.mediaSrc(this.heroBackground);
      return source ? { backgroundImage: `url(${source})` } : null;
    },
    skyFightBackgroundStyle() {
      const source = this.$options.filters.mediaSrc(this.skyFightBackground);
      return source ? { backgroundImage: `url(${source})` } : null;
    },
  },
  methods: {
    playAudio() {
      const audio = document.getElementById('audio-player');
      audio.play();
    },
    handleKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.playAudio();
      }
    },
    prev() {
      this.heroIndex = this.heroIndex === 1 ? 2 : 1;
    },
    next() {
      this.heroIndex = this.heroIndex === 1 ? 2 : 1;
    },
    goToInstagram() {
      window.open('https://www.instagram.com/wereckon/', '_blank', 'noopener');
    },
    scrollToSection(sectionId) {
      const section = document.getElementById(sectionId);
      if (!section) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      section.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    },
    navigateTo(destination) {
      if (typeof destination === 'object') {
        const resolvedDestination = this.$router.resolve(destination).route;
        if (this.$route.fullPath !== resolvedDestination.fullPath) this.$router.push(destination);
        return;
      }

      if (destination.startsWith('/')) {
        if (this.$route.path !== destination) this.$router.push(destination);
        return;
      }

      if (this.$route.name !== destination) this.$router.push({ name: destination });
    },
  },
};
</script>

<style scoped>
.home-page {
  background: #050505;
  color: #fff;
}

.text-h3,
.text-h2,
.text-h1 {
  font-family: unset;
}

.left-border {
  border-left: 4px solid #f02020;
  padding-left: 10px;
}

.definition-header {
  font-family: "Playfair Display", serif !important;
  font-size: 2rem !important;
}

.definition-verb {
  font-family: "Playfair Display", serif !important;
  font-size: 1.2rem !important;
}

.definition-meaning {
  font-size: 1.2rem;
}

.definition {
  background: unset;
}

.syllables {
  color: #838383;
  font-size: 1rem;
}

.syllables-audio {
  background-color: #333;
  border: 1px solid transparent;
  color: #fff;
  cursor: pointer;
  font-size: 1rem;
}

.syllables-audio:focus-visible {
  border-color: #f02020;
  outline: 2px solid #fff;
  outline-offset: 3px;
}

.hero-container {
  display: flex;
  max-height: 500px;
  width: auto;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.hero-content {
  background-color: rgba(43, 43, 43, .3);
  margin: 0 auto;
  max-height: 500px;
  max-width: 1200px;
  min-height: 450px;
  padding: 30px;
  width: 100%;
}

.hero-container-2 {
  display: flex;
  max-height: 450px;
  width: auto;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.hero-content-2 {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin: 0 auto;
  max-height: 450px;
  max-width: 1000px;
  min-height: 450px;
  padding: 30px;
  width: 100%;
}

.hero-2-title-container {
  font-size: 3rem;
  font-weight: 900;
  text-align: end;
}

.hero-2-title {
  background-color: #333;
  font-size: 3rem;
  font-weight: 900;
  padding: 25px;
}

.home-transition {
  align-items: center;
  background: #050505;
  border: 0;
  border-bottom: 1px solid #252525;
  color: #a9a9a9;
  cursor: pointer;
  display: flex;
  font-family: "Oswald", sans-serif;
  font-size: .85rem;
  gap: 14px;
  justify-content: center;
  letter-spacing: .12em;
  min-height: 82px;
  padding: 20px 24px;
  text-align: center;
  text-transform: uppercase;
  transition: background-color 160ms ease, color 160ms ease;
  width: 100%;
}

.home-transition:hover,
.home-transition:focus-visible {
  background: #0e0e0e;
  color: #fff;
}

.home-transition:focus-visible {
  outline: 2px solid #f02020;
  outline-offset: -4px;
}

.home-transition .v-icon {
  transition: transform 160ms ease;
}

.home-transition:hover .v-icon,
.home-transition:focus-visible .v-icon {
  transform: translateY(4px);
}

.home-section {
  padding: 112px 0;
  scroll-margin-top: 72px;
}

.home-section--dark {
  background: #050505;
}

.home-section--charcoal {
  background: #111;
}

.home-section--crew {
  padding-top: 72px;
}

.home-section--editorial {
  background: radial-gradient(circle at 82% 15%, #33110f 0, #090909 48%);
  border-bottom: 1px solid #2c2c2c;
  border-top: 1px solid #2c2c2c;
}

.home-section__container {
  max-width: 1200px;
}

.home-section__copy {
  max-width: 580px;
}

.split-copy--right {
  padding-left: 56px;
}

.split-copy--left {
  padding-right: 56px;
}

.home-section__eyebrow,
.story-card__category {
  color: #f02020;
  font-family: "Oswald", sans-serif;
  font-size: .82rem;
  font-weight: 700;
  letter-spacing: .15em;
  margin: 0 0 16px;
  text-transform: uppercase;
}

.home-section__title {
  color: inherit;
  font-family: "Oswald", sans-serif;
  font-size: clamp(2.5rem, 5vw, 4.8rem);
  font-weight: 700;
  letter-spacing: -.02em;
  line-height: 1.03;
  margin: 0 0 26px;
  max-width: 900px;
}

.home-section__body {
  color: #bdbdbd;
  font-size: clamp(1.05rem, 1.8vw, 1.3rem);
  line-height: 1.7;
  margin: 0;
  max-width: 720px;
}

.home-section__body--stacked p {
  margin: 0 0 18px;
}

.home-section__body--stacked p:last-child {
  margin-bottom: 0;
}

.home-section__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 34px;
}

.home-section__actions--centered {
  justify-content: center;
  margin-top: 48px;
}

.purpose-visual {
  background: #0d0d0d;
  border: 1px solid #333;
  margin: 0;
  padding: 10px;
}

.purpose-visual__image {
  background: #191919;
}

.purpose-visual figcaption {
  color: #a9a9a9;
  font-family: "Oswald", sans-serif;
  font-size: .82rem;
  letter-spacing: .12em;
  padding: 18px 10px 9px;
  text-transform: uppercase;
}

.team-image-button,
.story-card__link,
.capability-card__link,
.adventure-card__link {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: block;
  padding: 0;
  text-align: left;
  width: 100%;
}

.team-image-button:focus-visible,
.story-card__link:focus-visible,
.capability-card__link:focus-visible,
.adventure-card__link:focus-visible {
  outline: 3px solid #f02020;
  outline-offset: 4px;
}

.split-section-image {
  background: #191919;
  border: 1px solid #303030;
}

.team-image-button {
  background: #0d0d0d;
  border: 1px solid #333;
  padding: 10px;
  transition: border-color 160ms ease;
}

.team-image-button:hover {
  border-color: #4a4a4a;
}

.team-image-button .split-section-image {
  border: 0;
}

.editorial-intro {
  margin-bottom: 54px;
  max-width: 760px;
}

.reckon-way-grid {
  display: grid;
  gap: 22px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.story-card {
  background: #0a0a0a;
  border: 1px solid #492020;
  min-width: 0;
}

.story-card__image,
.home-card__image {
  background: #202020;
}

.story-card__content,
.home-card__content {
  display: flex;
  flex-direction: column;
  padding: 24px;
}

.story-card__title,
.adventure-card__title {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: clamp(1.5rem, 2.5vw, 2.2rem);
  font-weight: 700;
  line-height: 1.15;
}

.story-card__cta,
.capability-card__cta,
.adventure-card__cta {
  align-items: center;
  color: #c8c8c8;
  display: flex;
  font-size: .92rem;
  gap: 8px;
  margin-top: 22px;
}

.story-card__link,
.capability-card__link,
.adventure-card__link {
  transition: background-color 160ms ease, transform 160ms ease;
}

.story-card__link:hover,
.story-card__link:focus-visible,
.adventure-card__link:hover,
.adventure-card__link:focus-visible {
  background: #171717;
}

.centered-intro {
  margin: 0;
  max-width: 900px;
  text-align: left;
}

.centered-intro .home-section__body {
  margin: 0;
  text-align: left;
}

.capability-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 54px;
}

.home-card {
  border: 1px solid #303030;
  min-width: 0;
}

.capability-card {
  background: #0a0a0a;
  border-color: #343434;
}

.capability-card__link {
  min-height: 270px;
  padding: 28px;
}

.capability-card__link:hover,
.capability-card__link:focus-visible {
  background: #171717;
}

.capability-card__number {
  color: #f02020;
  display: block;
  font-family: "Oswald", sans-serif;
  font-size: .9rem;
  margin-bottom: 34px;
}

.capability-card__title {
  color: #fff;
  display: block;
  font-family: "Oswald", sans-serif;
  font-size: 1.55rem;
  font-weight: 700;
  line-height: 1.18;
}

.capability-card__identity {
  color: #e13a3a;
  display: block;
  font-family: "Oswald", sans-serif;
  font-size: .75rem;
  font-weight: 700;
  letter-spacing: .12em;
  margin-top: 8px;
  text-transform: uppercase;
}

.capability-card__description {
  color: #bdbdbd;
  display: block;
  line-height: 1.55;
  margin-top: 16px;
}

.capability-card__cta {
  color: #c8c8c8;
}

.stories-heading {
  display: flex;
  justify-content: space-between;
  margin-bottom: 50px;
}

.adventure-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.adventure-card {
  background: #101010;
}

.adventure-card__title {
  font-size: 1.65rem;
}

.final-cta {
  align-items: center;
  background: radial-gradient(circle at 82% 15%, #33110f 0, #090909 48%);
  border-bottom: 1px solid #2c2c2c;
  border-top: 1px solid #2c2c2c;
  display: flex;
  min-height: calc(100vh - 72px);
  text-align: center;
}

.final-cta__content {
  align-items: center;
  display: flex;
  flex-direction: column;
}

.final-cta .home-section__body {
  color: #d1d1d1;
  margin: 0 auto;
  text-align: left;
}

@media (max-width: 959px) {
  .home-section {
    padding: 80px 0;
  }

  .split-copy--right,
  .split-copy--left {
    max-width: none;
    padding-left: 12px;
    padding-right: 12px;
    padding-top: 34px;
  }

  .capability-grid,
  .adventure-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 599px) {
  .hero-content {
    min-height: 430px;
    padding: 20px 8px;
  }

  .hero-content .col-8 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .hero-2-title,
  .hero-2-title-container {
    font-size: 2rem;
  }

  .hero-2-title {
    padding: 15px;
  }

  .home-transition {
    font-size: .72rem;
    padding-left: 24px;
    padding-right: 24px;
  }

  .home-section {
    padding: 62px 0;
  }

  .home-section--crew {
    padding-top: 48px;
  }

  .home-section__container {
    padding-left: 24px;
    padding-right: 24px;
  }

  .home-section__title {
    font-size: clamp(2.35rem, 12vw, 3.35rem);
  }

  .reckon-way-grid,
  .capability-grid,
  .adventure-grid {
    grid-template-columns: 1fr;
  }

  .capability-card__link {
    min-height: 0;
  }

  .home-section__actions .v-btn {
    min-height: 48px;
  }
}
</style>
